import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Avatar, Badge, Button, Card, EmptyState, Modal, PageHeader, TableSkeleton } from "../components/ui";
import { isAccountAdmin, isManagerOrAbove } from "../lib/people";
import { buildOrgChart, ownerFirst, type OrgChartNode } from "../lib/orgChart";
import type { Branch, Department, DepartmentWrite, EmployeeFull } from "../api/types";

type ManagementTab = "people" | "departments" | "locations";

/** The shared source of truth: live employment records, read wide. */
function useOrganization() {
  return useQuery({
    queryKey: ["org-employees"],
    queryFn: ({ signal }) => api.employeeList({ page_size: 200, ordering: "last_name" }, signal),
    staleTime: 60_000,
  });
}

/**
 * Organization management: the team, how it is grouped and where it works.
 *
 * Deliberately separate from the Organization chart. Managing the structure
 * (departments, locations, people) and reading the reporting hierarchy are two
 * different jobs, so they are two views rather than two tabs of one.
 */
export function OrganizationPage({ initialTab = "people" }: { initialTab?: ManagementTab }) {
  const { user } = useAuth();
  const canManage = isManagerOrAbove(user?.role);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ManagementTab>(initialTab);

  const employees = useOrganization();
  const departments = useQuery({
    queryKey: ["departments", 100],
    queryFn: ({ signal }) => api.departmentsPage({ page_size: 100 }, signal),
  });
  const branches = useQuery({
    queryKey: ["branches"],
    queryFn: ({ signal }) => api.branches(signal),
  });

  const removeDepartment = useMutation({
    mutationFn: (id: string) => api.deleteDepartment(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["departments"] }),
  });

  const people = ownerFirst(
    (employees.data?.results ?? []).filter((p) => p.status !== "exited"),
  );

  return (
    <>
      <PageHeader
        title="Organization"
        subtitle="Your team, how it is grouped, and where it works."
      />

      <div className="mb-5 flex gap-1 border-b border-slate-200">
        {(
          [
            ["people", "People"],
            ["departments", "Departments"],
            ["locations", "Locations"],
          ] as Array<[ManagementTab, string]>
        ).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={
              tab === value
                ? "-mb-px border-b-2 border-slate-900 px-3 py-2 text-sm font-medium text-slate-900"
                : "-mb-px border-b-2 border-transparent px-3 py-2 text-sm text-slate-500 hover:text-slate-800"
            }
          >
            {label}
          </button>
        ))}
      </div>

      {employees.isPending ? (
        <TableSkeleton rows={6} />
      ) : employees.isError ? (
        <EmptyState
          title="Could not load the organization"
          body={employees.error instanceof ApiError ? employees.error.message : "Please try again."}
        />
      ) : tab === "people" ? (
        <PeopleView people={people} />
      ) : tab === "locations" ? (
        <LocationsView
          branches={branches.data ?? []}
          people={people}
          canManage={canManage}
        />
      ) : (
        <DepartmentsView
          departments={departments.data?.results ?? []}
          branches={branches.data ?? []}
          people={people}
          canManage={canManage}
          removing={removeDepartment.isPending}
          removeError={removeDepartment.error instanceof ApiError ? removeDepartment.error.message : null}
          onRemove={(id) => removeDepartment.mutate(id)}
        />
      )}
    </>
  );
}

/**
 * Organization chart: the reporting tree, read from each employee's
 * ``reporting_to``. There is no hard-coded tree and no management controls --
 * editing the structure belongs on Organization management.
 */
export function OrganizationChartPage() {
  const employees = useOrganization();
  const { user } = useAuth();
  const canAdmin = isAccountAdmin(user?.role);
  const people = (employees.data?.results ?? []).filter((p) => p.status !== "exited");

  return (
    <>
      <PageHeader
        title="Organization chart"
        subtitle="Who reports to whom, built from each employee's reporting line."
        actions={
          canAdmin ? (
            <Link
              to="/people/employees/new"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Plus size={15} /> Add employee
            </Link>
          ) : undefined
        }
      />
      {employees.isPending ? (
        <TableSkeleton rows={6} />
      ) : employees.isError ? (
        <EmptyState
          title="Could not load the organization chart"
          body={employees.error instanceof ApiError ? employees.error.message : "Please try again."}
        />
      ) : (
        <HierarchyView people={people} />
      )}
    </>
  );
}

function HierarchyView({ people }: { people: EmployeeFull[] }) {
  const chart = useMemo(() => buildOrgChart(people), [people]);

  if (people.length === 0) {
    return (
      <EmptyState
        title="No employees yet"
        body="Add employees and set who they report to; the hierarchy builds itself from that."
      />
    );
  }
  return (
    <div className="space-y-4">
      {chart.cycleMembers.length ? (
        <p role="status" className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {chart.cycleMembers.length} employee{chart.cycleMembers.length === 1 ? "" : "s"} {chart.cycleMembers.length === 1 ? "is" : "are"} in a
          circular reporting line. The loop is broken at the top so everyone still appears; fix the
          “Reports To” on those records to nest them properly.
        </p>
      ) : null}
      <Card>
        <div className="space-y-1">
          {chart.roots.map((node) => (
            <HierarchyNode key={node.employee.id} node={node} />
          ))}
        </div>
      </Card>
    </div>
  );
}

function HierarchyNode({ node }: { node: OrgChartNode }) {
  const { employee, depth, reports } = node;
  return (
    <>
      <Link
        to={`/people/employees/${employee.id}`}
        className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-slate-50"
        style={{ marginLeft: depth * 24 }}
      >
        <Avatar name={employee.display_name} src={employee.photo} size={36} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-slate-900">{employee.display_name}</span>
          <span className="block truncate text-xs text-slate-500">
            {employee.designation || "Not set"} · {employee.department_name || "No department"}
          </span>
          <span className="block truncate text-xs text-slate-400">
            {depth === 0 ? "Top level" : `Reports to ${employee.reporting_to_name || "not set"}`}
            {reports.length
              ? ` · ${reports.length} direct report${reports.length === 1 ? "" : "s"}`
              : ""}
          </span>
        </span>
        <Badge tone={employee.status === "active" ? "emerald" : "slate"}>
          {employee.status?.replace(/_/g, " ")}
        </Badge>
      </Link>
      {reports.map((report) => (
        <HierarchyNode key={report.employee.id} node={report} />
      ))}
    </>
  );
}

function PeopleView({ people }: { people: EmployeeFull[] }) {
  if (people.length === 0) {
    return <EmptyState title="No employees yet" body="Add employees to build the organization." />;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full min-w-[760px]">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="w-12 px-4 py-2.5" />
            <th className="px-4 py-2.5">Employee</th>
            <th className="px-4 py-2.5">ID</th>
            <th className="px-4 py-2.5">Designation</th>
            <th className="px-4 py-2.5">Department</th>
            <th className="px-4 py-2.5">Reports to</th>
            <th className="px-4 py-2.5">Type</th>
            <th className="px-4 py-2.5">Branch</th>
            <th className="px-4 py-2.5">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {people.map((person) => (
            <tr key={person.id} className="hover:bg-slate-50">
              <td className="px-4 py-3">
                <Link to={`/people/employees/${person.id}`}>
                  <Avatar name={person.display_name} src={person.photo} size={36} />
                </Link>
              </td>
              <td className="px-4 py-3">
                <Link
                  to={`/people/employees/${person.id}`}
                  className="text-sm font-medium text-slate-900 hover:underline"
                >
                  {person.display_name}
                </Link>
              </td>
              <td className="px-4 py-3 text-sm tabular-nums text-slate-600">{person.employee_code}</td>
              <td className="px-4 py-3 text-sm text-slate-600">{person.designation || "—"}</td>
              <td className="px-4 py-3 text-sm text-slate-600">{person.department_name || "—"}</td>
              <td className="px-4 py-3 text-sm text-slate-600">{person.reporting_to_name || "—"}</td>
              <td className="px-4 py-3 text-sm capitalize text-slate-600">
                {person.employment_type?.replace(/_/g, " ") || "—"}
              </td>
              <td className="px-4 py-3 text-sm text-slate-600">{person.branch_name || "—"}</td>
              <td className="px-4 py-3">
                <Badge tone={person.status === "active" ? "emerald" : "slate"}>
                  {person.status?.replace(/_/g, " ")}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DepartmentsView({
  departments,
  branches,
  people,
  canManage,
  removing,
  removeError,
  onRemove,
}: {
  departments: Department[];
  branches: Branch[];
  people: EmployeeFull[];
  canManage: boolean;
  removing: boolean;
  removeError: string | null;
  onRemove: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState<Department | null>(null);
  const create = useMutation({
    mutationFn: (body: { name: string; branch?: string | null; head_employee?: string | null }) =>
      api.createDepartment(body),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["departments"] });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<DepartmentWrite> }) =>
      api.updateDepartment(id, body),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ["departments"] });
    },
  });

  const byDepartment = useMemo(() => {
    const map = new Map<string, EmployeeFull[]>();
    for (const person of people) {
      const key =
        person.department_name || (person.is_account_owner ? "Owner / unassigned" : "Unassigned");
      const bucket = map.get(key) ?? [];
      bucket.push(person);
      map.set(key, bucket);
    }
    return map;
  }, [people]);

  const deletingActiveCount = deleting
    ? people.filter(
        (person) =>
          person.department_name === deleting.name &&
          (person.status === "active" || person.status === "notice_period"),
      ).length
    : 0;

  return (
    <>
      {canManage ? (
        <div className="mb-4 flex justify-end">
          {!open ? (
            <Button onClick={() => setOpen(true)}>
              <Plus size={14} /> Add department
            </Button>
          ) : null}
        </div>
      ) : null}

      {open ? (
        <Card className="mb-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const form = event.currentTarget;
              const data = new FormData(form);
              create.mutate({
                name: String(data.get("name") ?? "").trim(),
                branch: String(data.get("branch") ?? "") || null,
                head_employee: String(data.get("head") ?? "") || null,
              });
              form.reset();
            }}
            className="grid gap-3 sm:grid-cols-3"
          >
            <input name="name" required placeholder="Department name" className={inputClass} />
            <select name="branch" className={inputClass} defaultValue="">
              <option value="">No branch</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <select name="head" className={inputClass} defaultValue="">
              <option value="">No head</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.display_name}
                </option>
              ))}
            </select>
            {create.error instanceof ApiError ? (
              <p role="alert" className="text-sm text-rose-600 sm:col-span-3">{create.error.message}</p>
            ) : null}
            <div className="flex justify-end gap-2 sm:col-span-3">
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" variant="primary" disabled={create.isPending}>
                Save
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {removeError ? (
        <p role="alert" className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {removeError}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        {departments.length === 0 ? (
          <EmptyState
            title="No departments yet"
            body="Create departments such as Therapists or Front Desk to group employees."
          />
        ) : (
          <ul className="divide-y divide-slate-100">
            {departments.map((department) => {
              const members = byDepartment.get(department.name) ?? [];
              return (
                <li key={department.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{department.name}</p>
                      <p className="text-xs text-slate-500">
                        {department.head_name || "No head"} · {members.length} employee
                        {members.length === 1 ? "" : "s"}
                        {department.branch_name ? ` · ${department.branch_name}` : ""}
                      </p>
                    </div>
                    {canManage ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          title="Edit department"
                          aria-label={`Edit ${department.name}`}
                          onClick={() => setEditing(department)}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          title="Delete department"
                          aria-label={`Delete ${department.name}`}
                          disabled={removing}
                          onClick={() => setDeleting(department)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  {members.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {members.map((person) => (
                        <Link
                          key={person.id}
                          to={`/people/employees/${person.id}`}
                          className="inline-flex items-center gap-2 rounded-full bg-slate-50 py-0.5 pl-0.5 pr-2.5 text-xs text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-100"
                        >
                          <Avatar name={person.display_name} src={person.photo} size={22} />
                          {person.display_name}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {editing ? (
        <Modal title="Edit department" onClose={() => setEditing(null)}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const form = event.currentTarget;
              const data = new FormData(form);
              update.mutate({
                id: editing.id,
                body: {
                  name: String(data.get("name") ?? "").trim(),
                  code: String(data.get("code") ?? "").trim(),
                  description: String(data.get("description") ?? "").trim(),
                  branch: String(data.get("branch") ?? "") || null,
                  head_employee: String(data.get("head") ?? "") || null,
                  is_active: data.get("is_active") === "on",
                },
              });
            }}
            className="grid gap-3"
          >
            <input
              name="name"
              required
              defaultValue={editing.name}
              placeholder="Department name"
              className={inputClass}
            />
            <input
              name="code"
              defaultValue={editing.code ?? ""}
              placeholder="Code (e.g. FRT)"
              className={inputClass}
            />
            <input
              name="description"
              defaultValue={editing.description ?? ""}
              placeholder="Description"
              className={inputClass}
            />
            <select name="branch" className={inputClass} defaultValue={editing.branch ?? ""}>
              <option value="">No branch</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <select name="head" className={inputClass} defaultValue={editing.head_employee ?? ""}>
              <option value="">No head</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.display_name}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" name="is_active" defaultChecked={editing.is_active} />
              Active
            </label>
            {update.error instanceof ApiError ? (
              <p role="alert" className="text-sm text-rose-600">
                {update.error.message}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" variant="primary" disabled={update.isPending}>
                Save
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      {deleting ? (
        <Modal title="Delete department" onClose={() => setDeleting(null)}>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              Delete <span className="font-medium">{deleting.name}</span>?
            </p>
            <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Deleting a department cannot be undone.{" "}
              {deletingActiveCount > 0
                ? `${deletingActiveCount} active ${deletingActiveCount === 1 ? "employee is" : "employees are"} currently assigned; reassign them before deleting.`
                : "Retired departments can no longer be assigned to employees."}
            </p>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setDeleting(null)}>Cancel</Button>
              <Button
                variant="primary"
                disabled={removing}
                onClick={() => {
                  onRemove(deleting.id);
                  setDeleting(null);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function LocationsView({
  branches,
  people,
  canManage,
}: {
  branches: Branch[];
  people: EmployeeFull[];
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["branches"] });

  const create = useMutation({
    mutationFn: (body: { name: string; code?: string; address?: string; phone?: string }) =>
      api.createBranch(body),
    onSuccess: () => {
      setOpen(false);
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not save."),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteBranch(id),
    onSuccess: invalidate,
  });

  const byBranch = new Map<string, number>();
  for (const person of people) {
    if (person.branch) byBranch.set(person.branch, (byBranch.get(person.branch) ?? 0) + 1);
  }

  const createError = create.error instanceof ApiError ? create.error : null;
  const fieldErrors = createError?.fields ?? {};

  return (
    <>
      {canManage ? (
        <div className="mb-4 flex justify-end">
          {!open ? (
            <Button onClick={() => setOpen(true)}>
              <Plus size={14} /> Add location
            </Button>
          ) : null}
        </div>
      ) : null}

      {open ? (
        <Card className="mb-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              create.mutate({
                name: String(data.get("name") ?? "").trim(),
                code: String(data.get("code") ?? "").trim(),
                address: String(data.get("address") ?? "").trim(),
                phone: String(data.get("phone") ?? "").trim(),
              });
            }}
            className="grid gap-3 sm:grid-cols-2"
          >
            <input name="name" required placeholder="Branch / location name" className={inputClass} />
            <div>
              <input
                name="code"
                placeholder="Code (e.g. MGR)"
                className={inputClass}
                aria-invalid={fieldErrors.code ? true : undefined}
              />
              {fieldErrors.code ? (
                <p role="alert" className="mt-1 text-xs text-rose-600">
                  {fieldErrors.code.join(" ")}
                </p>
              ) : null}
            </div>
            <input name="address" placeholder="Address" className={inputClass} />
            <input name="phone" placeholder="Phone" className={inputClass} />
            {error && !Object.keys(fieldErrors).length ? (
              <p role="alert" className="text-sm text-rose-600 sm:col-span-2">{error}</p>
            ) : null}
            <div className="flex justify-end gap-2 sm:col-span-2">
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" variant="primary" disabled={create.isPending}>
                Save location
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        {branches.length === 0 ? (
          <EmptyState
            title="No locations yet"
            body="Add the branches or sites your team works from, then assign employees to them."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Location</th>
                <th className="px-4 py-2.5">Code</th>
                <th className="px-4 py-2.5">Address</th>
                <th className="px-4 py-2.5">Employees</th>
                <th className="px-4 py-2.5">Status</th>
                {canManage ? <th className="px-4 py-2.5 text-right" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {branches.map((branch) => (
                <tr key={branch.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{branch.name}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">{branch.code || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{branch.address || "—"}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">{byBranch.get(branch.id) ?? 0}</td>
                  <td className="px-4 py-3">
                    <Badge tone={branch.is_active ? "emerald" : "slate"}>
                      {branch.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  {canManage ? (
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        title="Delete location"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(branch.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const inputClass =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";
